import * as yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetLotteriesForEventQuery, GetLotteryQuery as GetLottery } from '__generated__/graphql';

import { encodeUrl, getServerErrors } from '../../../common/helpers';
import UI from '../../../common/UI';
import useEditLotteryMutation from './useEditLotteryMutation';
import useIntegerInput from '../../../common/useIntegerInput';
import useLocale from '../../../common/useLocale';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useProject from '../../useProject';
import useValidation from '../../../common/useValidation';

type Lottery = GetLottery['project']['lottery'];

const getEditLotteryResultSchema = (requireDrawSize: boolean) => yup.object().shape({
  ...requireDrawSize ? {
    registrations: yup.string().ensure().required(),
    draw_size: yup.number().required().min(0),
  } : {
    registrations: yup.string().nullable(),
    rejected_registrations: yup.string().nullable(),
  },
});

export interface EditLotteryResultFormProps {
  lottery: Lottery;
  event: GetLotteriesForEventQuery['project']['event'];
}

const EditLotteryResultForm = ({ lottery, event }: EditLotteryResultFormProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();
  const integerInput = useIntegerInput();

  const getValues = (lottery: Lottery) => ({
    perform_draw: !lottery.selected_registration_numbers && !lottery.rejected_registration_numbers,
    draw_size: null as null | number,
    registrations: '',
    selected_registrations: lottery.selected_registration_numbers,
    rejected_registrations: lottery.rejected_registration_numbers,
  });

  const form = useForm({
    values: getValues(lottery),
  });

  useNavigationPrompt(form.changed());

  const [success, setSuccess] = useState(false);

  const [editLottery, { data, error, loading }] = useEditLotteryMutation({
    input: {
      id: lottery.id,
      draw_result: {
        registrations: form.values.perform_draw ? form.values.registrations : form.values.selected_registrations,
        rejected_registrations: form.values.perform_draw ? null : form.values.rejected_registrations,
        draw_size: form.values.perform_draw ? form.values.draw_size : null,
      },
    },
    onSuccess: (lottery) => {
      form.reset(getValues(lottery));
      setSuccess(!!lottery.selected_registration_numbers || !!lottery.rejected_registration_numbers);
    },
  });

  const changed = form.changed();

  useEffect(() => {
    if (data && changed) {
      setSuccess(false);
    }
  }, [data, changed]);

  const { valid, errors } = useValidation({
    schema: getEditLotteryResultSchema(form.values.perform_draw),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const splitEligibleRegistrations = useMemo(
    () => form.values.registrations.split('\n'),
    [form.values.registrations],
  );

  const eligibleCount = useMemo(
    () => splitEligibleRegistrations.filter((value) => value.trim() !== '').length,
    [splitEligibleRegistrations],
  );

  const splitSelectedRegistrations = useMemo(
    () => form.values.selected_registrations.split('\n'),
    [form.values.selected_registrations],
  );

  const selectedCount = useMemo(
    () => splitSelectedRegistrations.filter((value) => value.trim() !== '').length,
    [splitSelectedRegistrations],
  );

  const splitRejectedRegistrations = useMemo(
    () => form.values.rejected_registrations.split('\n'),
    [form.values.rejected_registrations],
  );

  const rejectedCount = useMemo(
    () => splitRejectedRegistrations.filter((value) => value.trim() !== '').length,
    [splitRejectedRegistrations],
  );

  return (
    <UI.Form onSubmit={() => editLottery()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('draw_result')}
          </UI.Legend>
          <UI.InputDescription>
            {t('lottery_draw_result_description')}
          </UI.InputDescription>
        </UI.Div>

        {success && (
          <UI.Success>
            <UI.Strong sc={{ block: true }}>
              {t('draw_result_ready')}
            </UI.Strong>
          </UI.Success>
        )}

        <UI.InputGroup>
          <UI.FormGrid sc={{ gutter: 0.25 }}>
            <UI.Radio
              checked={form.values.perform_draw}
              onChange={() => form.set('perform_draw', true)}
              sc={{ box: true }}
            >
              <UI.Strong>
                {t('perform_random_draw')}
              </UI.Strong>
              <UI.Div>
                {t('perform_random_draw_description')}
              </UI.Div>
            </UI.Radio>
            <UI.Radio
              checked={!form.values.perform_draw}
              onChange={() => form.set('perform_draw', false)}
              sc={{ box: true }}
            >
              <UI.Strong>
                {t('enter_draw_result')}
              </UI.Strong>
              <UI.Div>
                {t('enter_draw_result_description')}
              </UI.Div>
            </UI.Radio>
          </UI.FormGrid>
        </UI.InputGroup>

        {form.values.perform_draw && (
          <UI.InputGroup sc={{ valid: !errors.draw_size, touched: form.getTouched('draw_size') }}>
            <UI.InputLabel htmlFor="LotteryDrawSize">
              {t('draw_size')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
              <UI.DebouncedInput
                {...form.text('draw_size', { inputHandler: integerInput() })}
                format={integerInput().format}
                maxLength={10}
                id="LotteryDrawSize"
                inputMode="numeric"
                match={/[\d]/g}
              />
            </UI.FormGrid>
            <UI.ErrorMessages attribute={t('draw_size')} errors={errors.draw_size} />
          </UI.InputGroup>
        )}

        {form.values.perform_draw && (
          <UI.InputGroup
            sc={{ valid: !errors.registrations, touched: form.getTouched('registrations') }}
          >
            <UI.InputLabel htmlFor="LotteryRegistrations">
              {t('eligible_registration_numbers')}
              {' '}
              <UI.Label sc={{ small: true, muted: eligibleCount === 0 }}>
                {formatNumber(eligibleCount)}
              </UI.Label>
              {form.values.perform_draw && (
                <>
                  {' '}
                  <UI.RequiredMark />
                </>
              )}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('eligible_registration_numbers_description')}
            </UI.InputDescription>
            <UI.TextArea
              {...form.textarea('registrations')}
              id="LotteryRegistrations"
              style={{ fontFamily: 'monospace' }}
              placeholder="ATL1&#10;ATL2&#10;ATL3"
              rows={Math.min(Math.max(splitEligibleRegistrations.length, 3), 20)}
            />
            <UI.ErrorMessages
              attribute={t('eligible_registration_numbers')}
              errors={errors.registrations}
            />
          </UI.InputGroup>
        )}

        {!form.values.perform_draw && (
          <>
            <UI.InputGroup
              sc={{ valid: !errors.selected_registrations, touched: form.getTouched('selected_registrations') }}
            >
              <UI.InputLabel htmlFor="LotterySelectedRegistrations">
                {t('selected_registration_numbers')}
                {' '}
                <UI.Label sc={{ small: true, muted: selectedCount === 0 }}>
                  {formatNumber(selectedCount)}
                </UI.Label>
              </UI.InputLabel>
              <UI.InputDescription>
                {t('selected_registration_numbers_description')}
              </UI.InputDescription>
              <UI.TextArea
                {...form.textarea('selected_registrations')}
                id="LotterySelectedRegistrations"
                style={{ fontFamily: 'monospace' }}
                placeholder="ATL1&#10;ATL2&#10;ATL3"
                rows={Math.min(Math.max(splitSelectedRegistrations.length, 3), 20)}
              />
              <UI.ErrorMessages
                attribute={t('selected_registration_numbers')}
                errors={errors.selected_registrations}
              />
            </UI.InputGroup>
            <UI.InputGroup
              sc={{ valid: !errors.rejected_registrations, touched: form.getTouched('rejected_registrations') }}
            >
              <UI.InputLabel htmlFor="LotteryRejectedRegistrations">
                {t('rejected_registration_numbers')}
                {' '}
                <UI.Label sc={{ small: true, muted: rejectedCount === 0 }}>
                  {formatNumber(rejectedCount)}
                </UI.Label>
              </UI.InputLabel>
              <UI.InputDescription>
                {t('rejected_registration_numbers_description')}
              </UI.InputDescription>
              <UI.TextArea
                {...form.textarea('rejected_registrations')}
                id="LotteryRejectedRegistrations"
                style={{ fontFamily: 'monospace' }}
                placeholder="ATL4&#10;ATL5&#10;ATL6"
                rows={Math.min(Math.max(splitRejectedRegistrations.length, 3), 20)}
              />
              <UI.ErrorMessages
                attribute={t('rejected_registration_numbers')}
                errors={errors.rejected_registrations}
              />
            </UI.InputGroup>
          </>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={form.values.perform_draw
            ? t('perform_draw')
            : (selectedCount + rejectedCount > 0 ? t('save_draw_result') : t('revoke_draw_result'))}
          closeTo={`/${project.id}/participants/lotteries?${encodeUrl({ event: event.id })}`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditLotteryResultForm;
